import { ReactNode, useMemo, useState } from 'react';

import {
  Add,
  Favorite,
  FavoriteBorder,
  LightbulbOutlined,
  Visibility,
} from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  isMonitorPlanLimitError,
  UpgradeModal,
} from 'src/pages/Monitor/components/UpgradeModal';
import { useMonitorLimits } from 'src/pages/Monitor/hooks/useMonitorLimits';
import { getIsFic } from 'src/services/meta-carteiras-fic-fidcs';
import {
  FicChildrenResponse,
  followFund,
  getFollowedFunds,
  getOperationFicChildren,
  getOperationMonitoredWallets,
  unfollowFund,
} from 'src/services/wallet';
import {
  FOLLOWED_FUNDS_QUERY_PREFIX,
  followedFundsQueryKey,
  FOLLOWING_FIC_DASHBOARD_SUMMARY_QUERY_PREFIX,
} from 'src/services/wallet/queryKeys';
import { getStrapiValidationMessage } from 'src/utils/getStrapiValidationMessage';

import { AddToWalletModal } from './AddToWalletModal';
import { MonitorStepsPopover } from './MonitorStepsPopover';
import { MonitorSuccessModal } from './MonitorSuccessModal';

const FOLLOW_GENERIC_ERROR =
  'Não foi possível concluir a ação. Tente novamente.';

interface MonitorFundButtonProps {
  operationId: string;
  operationName: string;
  stepsToShow?: ('see' | 'follow' | 'monitor')[];
}

interface MonitorActionProps {
  icon: ReactNode;
  label: string;
  tooltip: string;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

function MonitorAction({
  icon,
  label,
  tooltip,
  active = false,
  disabled = false,
  onClick,
}: MonitorActionProps) {
  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <ButtonBase
          onClick={onClick}
          disabled={disabled || !onClick}
          aria-label={label}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 1,
            minWidth: 64,
            borderRadius: 2,
            color: active ? 'primary.main' : 'text.secondary',
            transition: 'background-color 0.2s',
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            },
            '&.Mui-disabled': { color: 'text.disabled' },
          }}>
          <Box
            sx={{
              display: 'inline-flex',
              color: disabled ? 'action.disabled' : 'primary.main',
            }}>
            {icon}
          </Box>
          <Typography
            sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1 }}>
            {label}
          </Typography>
        </ButtonBase>
      </Box>
    </Tooltip>
  );
}

export function MonitorFundButton({
  operationId,
  operationName,
  stepsToShow,
}: MonitorFundButtonProps) {
  const { auth } = useAuthContext();
  const {
    limits,
    monitorLimits,
    isLoadingMonitorLimits,
    refetchMonitorLimits,
    validateWalletCreation,
  } = useMonitorLimits();
  const isLoggedIn = Boolean(auth?.isLoggedIn);
  const disabled = !isLoggedIn;
  const queryClient = useQueryClient();

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [followUpgradeModal, setFollowUpgradeModal] = useState<{
    open: boolean;
    apiMessage: string | null;
  }>({ open: false, apiMessage: null });
  const [monitorUpgradeModal, setMonitorUpgradeModal] = useState({
    open: false,
    maxLimit: null as number | null,
    currentCount: 0,
  });
  const [stepsAnchorEl, setStepsAnchorEl] = useState<HTMLElement | null>(null);
  const [successState, setSuccessState] = useState<{
    open: boolean;
    message: React.ReactNode;
    walletId?: number | null;
  }>({ open: false, message: null, walletId: null });

  const ficChildrenFallback = useMemo<FicChildrenResponse>(
    () => ({
      operation: {
        id: 0,
        hashId: operationId,
        name: operationName,
        cnpj: '',
      },
      isFic: false,
      children: [],
    }),
    [operationId, operationName],
  );

  const { data: monitoredWallets = [], isLoading } = useQuery(
    ['operation-monitored-wallets', operationId],
    () => getOperationMonitoredWallets(operationId),
    { enabled: Boolean(operationId) && isLoggedIn },
  );

  const isMonitored = monitoredWallets.length > 0;

  const { data: followedFundsResponse } = useQuery(
    followedFundsQueryKey(auth?.user?.id),
    getFollowedFunds,
    { enabled: isLoggedIn, refetchOnWindowFocus: true },
  );
  const followedFunds = followedFundsResponse?.wallet;

  const ficChildrenQuery = useQuery(
    ['operation-fic-children', operationId],
    () => getOperationFicChildren(operationId),
    {
      enabled: Boolean(operationId) && isLoggedIn,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 1000 * 60 * 5,
    },
  );

  const isFicQuery = useQuery(
    ['operation-is-fic', operationId],
    () => getIsFic(operationId),
    {
      enabled: Boolean(operationId) && isLoggedIn,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 1000 * 60 * 5,
    },
  );

  const isFollowed = Boolean(
    followedFunds?.walletFunds?.some(
      (fund) => fund.operation?.hashId === operationId,
    ),
  );
  const canFollow = isFicQuery.isSuccess && isFicQuery.data?.isFic === true;

  const followedFundsCount =
    followedFundsResponse?.followCount ??
    followedFunds?.walletFunds?.filter((fund) => !fund.parentWalletFund)
      .length ??
    0;
  const followFundsAllowed = limits.hasLimits
    ? limits.followFunds === true
    : followedFundsResponse?.followFunds === true;
  const walletLimit = limits.hasLimits
    ? limits.maxWallets
    : followedFundsResponse?.limits?.walletQuantity ?? null;
  const walletUsage = limits.hasLimits
    ? limits.usageWallets
    : followedFundsResponse?.usage?.wallets ?? followedFundsCount;
  const isFollowLimitReached =
    !isFollowed &&
    (!followFundsAllowed ||
      (typeof walletLimit === 'number' && walletUsage >= walletLimit));
  const followLimitTooltip = !followFundsAllowed
    ? 'Seu plano não permite seguir fundos FIC. Clique para falar com o comercial.'
    : typeof walletLimit === 'number'
      ? `Você atingiu o limite de ${walletLimit} carteiras/FICs seguidos da assinatura. Clique para ver opções.`
      : 'Limite de acompanhamento atingido. Clique para ver opções.';

  const { mutate: toggleFollow, isLoading: isTogglingFollow } = useMutation({
    mutationFn: () =>
      isFollowed ? unfollowFund(operationId) : followFund(operationId),
    onSuccess: (response) => {
      if (!response?.success) {
        const errorMessage = response?.error || FOLLOW_GENERIC_ERROR;
        if (!isFollowed && isMonitorPlanLimitError(errorMessage)) {
          setFollowUpgradeModal({ open: true, apiMessage: errorMessage });
          return;
        }
        toast.error(errorMessage);
        return;
      }
      queryClient.invalidateQueries(FOLLOWED_FUNDS_QUERY_PREFIX);
      queryClient.invalidateQueries(
        FOLLOWING_FIC_DASHBOARD_SUMMARY_QUERY_PREFIX,
      );
      refetchMonitorLimits();
      const successMessage = isFollowed ? (
        'Você deixou de seguir esta operação.'
      ) : (
        <Box>
          <Typography variant="body1">
            Você começou a seguir esta operação.
          </Typography>
          <Typography variant="body2">
            Acesse a aba&nbsp;
            <Typography
              variant="body2"
              color="primary.main"
              component={RouterLink}
              sx={{ '&:hover': { textDecoration: 'underline' } }}
              aria-label="aba Seguindo"
              to="/monitor?tab=following">
              <b>Seguindo</b>
            </Typography>
            &nbsp;para acompanhá-la.
          </Typography>
        </Box>
      );
      toast.success(successMessage);
    },
    onError: (error: AxiosError) => {
      const apiMessage = getStrapiValidationMessage(
        error as Parameters<typeof getStrapiValidationMessage>[0],
        '',
      );

      if (!isFollowed && isMonitorPlanLimitError(apiMessage)) {
        setFollowUpgradeModal({ open: true, apiMessage });
        return;
      }

      toast.error(apiMessage || FOLLOW_GENERIC_ERROR);
    },
  });

  const handleFollowClick = () => {
    if (isFollowLimitReached) {
      setFollowUpgradeModal({
        open: true,
        apiMessage: !followFundsAllowed
          ? 'Seu plano não permite seguir fundos FIC.'
          : typeof walletLimit === 'number'
            ? `Limite de carteiras excedido. Uso atual: ${walletUsage} de ${walletLimit}. Tentativa de adicionar: 1.`
            : null,
      });
      return;
    }
    toggleFollow();
  };

  const handleAddSuccess = (
    message: React.ReactNode,
    walletId?: number | null,
  ) => {
    setAddModalOpen(false);
    setSuccessState({ open: true, message, walletId });
  };

  const handleMonitorClick = () => {
    if (isLoadingMonitorLimits || !monitorLimits) {
      refetchMonitorLimits();
      toast.info('Carregando limites do plano. Tente novamente em instantes.');
      return;
    }

    const walletValidation = validateWalletCreation(limits.usageWallets);
    if (!walletValidation.isValid) {
      setMonitorUpgradeModal({
        open: true,
        maxLimit: limits.maxWallets,
        currentCount: limits.usageWallets,
      });
      return;
    }

    setAddModalOpen(true);
  };

  const handleViewFund = () => {
    window.open(`/monitor/wallet/view/${operationId}`, '_blank');
  };

  const loginTooltip = 'Faça login para monitorar esta operação';

  const monitorIcon = isLoading ? (
    <CircularProgress size="1.25rem" color="inherit" />
  ) : isMonitored ? (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <Add fontSize="small" />
      <CheckCircleIcon
        sx={{
          position: 'absolute',
          top: -4,
          right: -6,
          fontSize: 14,
          color: 'success.main',
          bgcolor: 'background.paper',
          borderRadius: '50%',
        }}
      />
    </Box>
  ) : (
    <Add fontSize="small" />
  );

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
          px: 1,
          py: 1,
          pt: 1,
          borderRadius: 1,
          backgroundColor: 'background.paper',
        }}>
        <Tooltip title="Como funciona o monitoramento" arrow placement="top">
          <IconButton
            size="small"
            aria-label="Como funciona o monitoramento"
            onClick={(event) => setStepsAnchorEl(event.currentTarget)}
            sx={{
              position: 'absolute',
              top: 2,
              right: 4,
              color: 'primary.main',
              p: 0.25,
            }}>
            <LightbulbOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary',
            lineHeight: 1,
          }}>
          Monitoramento
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {stepsToShow?.includes('see') && (
            <MonitorAction
              icon={<Visibility fontSize="small" />}
              label="Ver"
              tooltip={disabled ? loginTooltip : 'Visualizar esta operação'}
              disabled={disabled}
              onClick={handleViewFund}
            />
          )}
          {canFollow &&
            (stepsToShow === undefined || stepsToShow.includes('follow')) && (
              <MonitorAction
                icon={
                  isTogglingFollow ? (
                    <CircularProgress size="1.25rem" color="inherit" />
                  ) : isFollowed ? (
                    <Favorite fontSize="small" />
                  ) : (
                    <FavoriteBorder fontSize="small" />
                  )
                }
                label={isFollowed ? 'Seguindo' : 'Seguir'}
                tooltip={
                  disabled
                    ? loginTooltip
                    : isFollowed
                      ? 'Você segue esta operação. Clique para deixar de seguir.'
                      : isFollowLimitReached
                        ? followLimitTooltip
                        : 'Siga esta operação para acompanhá-la na aba Seguindo do Monitor.'
                }
                active={isFollowed}
                disabled={disabled || isTogglingFollow}
                onClick={handleFollowClick}
              />
            )}
          <MonitorAction
            icon={monitorIcon}
            label={isMonitored ? 'Monitorando' : 'Monitorar'}
            tooltip={
              disabled
                ? loginTooltip
                : isMonitored
                  ? 'Você já monitora esta operação. Adicione-a a outra carteira.'
                  : 'Adiciona esta operação a uma das suas carteiras no monitor.'
            }
            active={isMonitored}
            disabled={disabled}
            onClick={handleMonitorClick}
          />
        </Stack>
      </Box>

      <AddToWalletModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        operationId={operationId}
        operationName={operationName}
        ficChildrenData={
          ficChildrenQuery.isError ? ficChildrenFallback : ficChildrenQuery.data
        }
        isLoadingFicChildren={ficChildrenQuery.isLoading}
        isFicChildrenError={ficChildrenQuery.isError}
        onRetryFicChildren={() => ficChildrenQuery.refetch()}
        onSuccess={handleAddSuccess}
      />

      <MonitorSuccessModal
        open={successState.open}
        message={successState.message}
        walletId={successState.walletId}
        onClose={() =>
          setSuccessState({ open: false, message: null, walletId: null })
        }
      />

      <MonitorStepsPopover
        open={Boolean(stepsAnchorEl)}
        anchorEl={stepsAnchorEl}
        onClose={() => setStepsAnchorEl(null)}
        stepsToShow={stepsToShow ?? ['see', 'follow', 'monitor']}
      />

      <UpgradeModal
        open={followUpgradeModal.open}
        onClose={() => setFollowUpgradeModal({ open: false, apiMessage: null })}
        type="follow"
        maxLimit={followFundsAllowed ? walletLimit : 0}
        currentCount={walletUsage}
        apiMessage={followUpgradeModal.apiMessage}
        userName={auth?.user?.name}
        userId={auth?.user?.id}
      />

      <UpgradeModal
        open={monitorUpgradeModal.open}
        onClose={() =>
          setMonitorUpgradeModal({
            open: false,
            maxLimit: null,
            currentCount: 0,
          })
        }
        type="wallet"
        maxLimit={monitorUpgradeModal.maxLimit}
        currentCount={monitorUpgradeModal.currentCount}
        attemptedCount={1}
        userName={auth?.user?.name}
        userId={auth?.user?.id}
      />
    </>
  );
}
